"use client"

import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import DealService from '@/api/deal.service';
import { Loader2, Plus, Filter, RefreshCcw } from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/ui/badge';
import FormModal from '@/components/FormModal';
import CreateDealForm from './CreateDealForm';
import CreatePipelineForm from './CreatePipelineForm';
import DealDetailForm from './DealDetailForm';
import DroppableStage from './DroppableStage';

export default function Deals() {
  const [pipeline, setPipeline] = useState<any>(null);
  const [deals, setDeals] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false);
  const [isCreatePipelineModalOpen, setIsCreatePipelineModalOpen] = useState(false);
  const [isDealDetailModalOpen, setIsDealDetailModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const { toast } = useToast();
  
  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;
  
  useEffect(() => {
    if (businessId) {
      fetchPipelineAndDeals();
    }
  }, [businessId]);
  
  const fetchPipelineAndDeals = async () => {
    setIsLoading(true);
    try {
      const pipelineResponse = await DealService.getPipeline(businessId);
      console.log("pipelineResponse.data", pipelineResponse.data);
      const dealsResponse = await DealService.getAllDeals(businessId);
      
      if (pipelineResponse.data.result === 'success') {
        setPipeline(pipelineResponse.data.dealPipeline);
      }
      console.log("dealsResponse.data", dealsResponse.data);
      if (dealsResponse.data.result === 'success') {
        setDeals(dealsResponse.data.deals);
      }
    } catch (error) {
      console.error("Error fetching pipeline and deals:", error);
      toast({
        title: "Error",
        description: "Failed to load pipeline and deals.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  

  
  const handleRefresh = () => {
    fetchPipelineAndDeals();
  };

  const [activeId, setActiveId] = useState<string | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDealClick = (deal: any) => {
    setSelectedDeal(deal);
    setIsDealDetailModalOpen(true);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const dealId = active.id as number;
    const overId = over.id as string;
    
    // Check if we're dropping on a stage
    if (overId.startsWith('stage-')) {
      const newStageId = parseInt(overId.split('-')[1]);
      const deal = deals.find(d => d.id === dealId);
      
      if (deal && deal.dealStageId !== newStageId) {
        // Optimistic update
        const updatedDeals = deals.map(d => {
          if (d.id === dealId) {
            return { ...d, dealStageId: newStageId };
          }
          return d;
        });
        
        setDeals(updatedDeals);
        
        // API call
        DealService.updateDealStage(dealId, newStageId)
          .then((response: any) => {
            if (response.data.result === 'success') {
              toast({
                title: "Success",
                description: "Deal moved to new stage successfully.",
              });
            } else {
              toast({
                title: "Error",
                description: response.data.message,
                variant: "destructive",
              });
            }
          })
          .catch((error) => {
            console.error("Error updating deal stage:", error);
            toast({
              title: "Error",
              description: "Failed to update deal stage.",
              variant: "destructive",
            });
            // Revert the UI change
            fetchPipelineAndDeals();
          });
      }
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading pipeline data...</p>
      </div>
    );
  }
  
  const noPipelineDefined = !pipeline || !pipeline.stages || pipeline.stages.length === 0;
  
  // If no pipeline is defined, show a message to create one
  if (noPipelineDefined) {
    return (
      <div className="container py-8 mx-auto">
        <div className="flex flex-col items-center justify-center h-[70vh] text-center">
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2">No Pipeline Defined</h2>
            <p className="text-muted-foreground max-w-md">
              You haven't created a sales pipeline yet. Set up stages to start tracking your deals.
            </p>
          </div>
          <Button 
            onClick={() => setIsCreatePipelineModalOpen(true)} 
            size="lg"
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Your First Pipeline
          </Button>
          
          <FormModal
            open={isCreatePipelineModalOpen}
            setOpen={setIsCreatePipelineModalOpen}
            title="Create Sales Pipeline"
          >
            <CreatePipelineForm 
              businessId={businessId} 
              onSuccess={() => {
                setIsCreatePipelineModalOpen(false);
                fetchPipelineAndDeals();
              }}
            />
          </FormModal>
        </div>
      </div>
    );
  }
  
  // Group deals by stage
  const dealsByStage = pipeline.stages.reduce((acc: any, stage: any) => {
    console.log("stage.id", stage.id);
    console.log("deals", deals);
    acc[stage.id] = deals.filter(deal => deal.dealStageId === stage.id);
    return acc;
  }, {});
  
  return (
    <div className="container py-6 mx-auto">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Deals Pipeline</h1>
            <p className="text-muted-foreground mt-1">Manage and track your sales pipeline</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={handleRefresh}
            >
              <RefreshCcw className="w-4 h-4" />
              Refresh
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  <Filter className="w-4 h-4" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>Filter Deals</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>All Deals</DropdownMenuItem>
                <DropdownMenuItem>My Deals</DropdownMenuItem>
                <DropdownMenuItem>High Value (&gt;$10k)</DropdownMenuItem>
                <DropdownMenuItem>Closing This Month</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              onClick={() => setIsCreateDealModalOpen(true)}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Deal
            </Button>
          </div>
        </div>
        
        {/* Kanban Board */}
        <div className="overflow-auto pb-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex space-x-4 min-w-max">
              {pipeline.stages.map((stage: any) => (
                <DroppableStage
                  key={stage.id}
                  stage={stage}
                  deals={dealsByStage[stage.id] || []}
                  onDealClick={handleDealClick}
                />
              ))}
            </div>
            
            <DragOverlay>
              {activeId ? (
                <div className="opacity-50">
                  {/* Render a preview of the dragged item */}
                  {(() => {
                    const draggedDeal = deals.find(d => d.id.toString() === activeId);
                    return draggedDeal ? (
                      <Card className="shadow-lg">
                        <CardHeader className="p-3 pb-0">
                          <CardTitle className="text-sm">{draggedDeal.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 pt-2">
                          <div className="text-sm font-medium">
                            ${draggedDeal.value.toLocaleString()}
                          </div>
                        </CardContent>
                      </Card>
                    ) : null;
                  })()}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
      
      {/* Create Deal Modal */}
      <FormModal
        open={isCreateDealModalOpen}
        setOpen={setIsCreateDealModalOpen}
        title="Create New Deal"
      >
        <CreateDealForm 
          businessId={businessId} 
          pipelineStages={pipeline.stages}
          onSuccess={() => {
            setIsCreateDealModalOpen(false);
            fetchPipelineAndDeals();
          }}
        />
      </FormModal>

      {/* Deal Detail Modal */}
      <FormModal
        open={isDealDetailModalOpen}
        setOpen={setIsDealDetailModalOpen}
        title="Deal Details"
      >
        {selectedDeal && (
          <DealDetailForm 
            deal={selectedDeal}
            pipelineStages={pipeline.stages}
            onSuccess={() => {
              setIsDealDetailModalOpen(false);
              setSelectedDeal(null);
              fetchPipelineAndDeals();
            }}
            onDelete={() => {
              setIsDealDetailModalOpen(false);
              setSelectedDeal(null);
              fetchPipelineAndDeals();
            }}
          />
        )}
      </FormModal>
    </div>
  );
}